import {useCallback, memo} from 'react';
import Countdown from 'react-countdown';
import {createSelector} from 'reselect';
import * as Sentry from '@sentry/react';
import {useTranslation} from 'react-i18next';

import {
    showToastAction,
    useAppDispatch,
    patchTodoActionSaga,
    setNumberOfTodosFinishedAction,
    pressPomodoroTimerAction,
    setActivePomodoroTabAction,
    selectPomodoroSettings,
} from 'store';
import {selectPomodoroCurrentTask, useAppSelector} from 'store';
import {WidgetRenderTimer} from './components/widgetRenderTimer';

const stateSelectorHandle = createSelector(
    selectPomodoroCurrentTask,
    selectPomodoroSettings,
    (currentPomodoroTask, pomodoroSettings) => {
        return {
            currentPomodoroTask,
            pomodoroSettings,
        };
    },
);

export const WidgetCounter = memo(
    ({
        refCallback,
        setTimerState,
        noOfPomodorosFinished,
        pomodoroActiveTab,
        pomodoroRunning,
    }: {
        refCallback: any;
        setTimerState: (state: 'PAUSE' | 'START') => void;
        noOfPomodorosFinished: number;
        pomodoroActiveTab: 'POMODORO' | 'SHORT_BREAK' | 'LONG_BREAK';
        pomodoroRunning: any;
    }) => {
        const dispatch = useAppDispatch();
        const stateSelector = useCallback(stateSelectorHandle, []);
        const {currentPomodoroTask, pomodoroSettings} =
            useAppSelector(stateSelector);
        const {t} = useTranslation();

        const onComplete = useCallback(() => {
            try {
                if (currentPomodoroTask)
                    dispatch(
                        patchTodoActionSaga({
                            todo: {
                                ...currentPomodoroTask,
                                completed_at: new Date(),
                            } as unknown as any,
                            directUpdate: true,
                        }),
                    );

                dispatch(
                    showToastAction({
                        horizontal: 'right',
                        vertical: 'top',
                        open: true,
                        title: t('pomodoro.congratulations'),
                        subTitle: `${t(
                            'pomodoro.youHaveFinishedOne',
                        )} ${pomodoroActiveTab
                            ?.toLowerCase()
                            .replace('_', ' ')}`,
                    }),
                );

                dispatch(
                    pressPomodoroTimerAction({
                        running: false,
                        timeLeft: {
                            pomodoro:
                                pomodoroActiveTab === 'POMODORO'
                                    ? pomodoroSettings?.pomodoro ?? 1500000
                                    : undefined,
                            shortBreak:
                                pomodoroActiveTab === 'SHORT_BREAK'
                                    ? pomodoroSettings?.shortBreak ?? 300000
                                    : undefined,
                            longBreak:
                                pomodoroActiveTab === 'LONG_BREAK'
                                    ? pomodoroSettings?.longBreak ?? 900000
                                    : undefined,
                        },
                    }),
                );

                refCallback.current.pause();

                dispatch(
                    setActivePomodoroTabAction(
                        pomodoroActiveTab === 'POMODORO' &&
                            noOfPomodorosFinished === 3
                            ? 'LONG_BREAK'
                            : pomodoroActiveTab === 'POMODORO'
                            ? 'SHORT_BREAK'
                            : pomodoroActiveTab === 'SHORT_BREAK'
                            ? 'LONG_BREAK'
                            : 'POMODORO',
                    ),
                );

                dispatch(
                    setNumberOfTodosFinishedAction(
                        noOfPomodorosFinished < 4
                            ? noOfPomodorosFinished + 1
                            : 1,
                    ),
                );
                setTimerState('START');
            } catch (error) {
                console.log({error});
                Sentry.captureException(error);
            }
        }, [
            currentPomodoroTask,
            dispatch,
            t,
            pomodoroActiveTab,
            pomodoroSettings?.pomodoro,
            pomodoroSettings?.shortBreak,
            pomodoroSettings?.longBreak,
            refCallback,
            noOfPomodorosFinished,
            setTimerState,
        ]);

        const alarm = async () => {
            try {
                const path = require('assets/audio/alarm.mp3').default;
                const audio = new Audio(path);
                audio.muted = false;
                await audio.play();
            } catch (error: any) {
                if (error?.name === 'NotAllowedError') {
                    console.error('Audio playback is blocked by the browser.');
                } else if (error?.name === 'AbortError') {
                    console.error('Playback was interrupted.');
                } else if (error?.name === 'NotSupportedError') {
                    console.error('Operation not supported.');
                } else {
                    Sentry.captureException(error);
                    console.error({error});
                }
                dispatch(
                    showToastAction({
                        title: t('common.anErrorHasOccurred'),
                        subTitle: t('common.pleaseTryAgain'),
                        open: true,
                        vertical: 'top',
                        horizontal: 'center',
                        icon: 'error',
                    }),
                );
            }
        };

        const renderer = useCallback(({minutes, seconds, completed}) => {
            if (completed) alarm();

            return (
                <WidgetRenderTimer
                    completed={completed}
                    minutes={minutes}
                    seconds={seconds}
                />
            );
        }, []);

        return (
            <Countdown
                date={
                    pomodoroActiveTab === 'POMODORO'
                        ? pomodoroRunning?.timeLeft?.pomodoro
                            ? Date.now() + pomodoroRunning?.timeLeft?.pomodoro
                            : Date.now() + 1500000
                        : pomodoroActiveTab === 'SHORT_BREAK'
                        ? pomodoroRunning?.timeLeft?.shortBreak
                            ? Date.now() + pomodoroRunning?.timeLeft?.shortBreak
                            : Date.now() + 300000
                        : pomodoroRunning?.timeLeft?.longBreak
                        ? Date.now() + pomodoroRunning?.timeLeft?.longBreak
                        : Date.now() + 900000
                }
                autoStart={false}
                onComplete={onComplete}
                // key={
                //     pomodoroActiveTab === 'POMODORO'
                //         ? pomodoroRunning?.timeLeft?.pomodoro
                //             ? (
                //                   Date.now() +
                //                   pomodoroRunning?.timeLeft?.pomodoro
                //               ).toString()
                //             : (Date.now() + 1500000).toString()
                //         : pomodoroActiveTab === 'SHORT_BREAK'
                //         ? pomodoroRunning?.timeLeft?.shortBreak
                //             ? (
                //                   Date.now() +
                //                   pomodoroRunning?.timeLeft?.shortBreak
                //               ).toString()
                //             : (Date.now() + 300000).toString()
                //         : pomodoroRunning?.timeLeft?.longBreak
                //         ? (
                //               Date.now() + pomodoroRunning?.timeLeft?.longBreak
                //           ).toString()
                //         : (Date.now() + 900000).toString()
                // }
                ref={refCallback}
                onStart={() => {
                    setTimerState('PAUSE');
                }}
                onPause={() => {
                    setTimerState('START');
                }}
                renderer={renderer}
            />
        );
    },
);
